//#region src/i18n.ts
/**
* Shared i18n-assets manifest cache + URL helpers used by both the
* `pixai-server` SSR runtime and the `pixai-app` client/SSR bundle.
*
* The manifest at `cdn.pixai.art/i18n-assets/manifest.json` maps each
* `(language, namespace)` pair to a content-hashed bundle path (e.g.
* `/i18n-assets/en/common.c9733eaed6.json`). The async loader resolves
* through it so translation files can ship with `Cache-Control: immutable`,
* and the sync helpers let render-time sites (preload / prefetch link
* tags) emit the same hashed URL when the cache is warm.
*
* This module uses only platform-agnostic globals (`fetch`,
* `AbortSignal.timeout`, `Date.now`) so the same body runs in Node and
* in the browser bundle. State is per-process — the client and SSR pod
* each maintain their own cache.
*/
const CDN_BASE = "https://cdn.pixai.art";
const I18N_MANIFEST_URL = `${CDN_BASE}/i18n-assets/manifest.json`;
const MANIFEST_FETCH_TIMEOUT_MS = 2e3;
const MANIFEST_TTL_MS = 6e4;
const MANIFEST_FAILURE_TTL_MS = 5e3;
/**
* Normalize a language code to the CDN convention.
*
* 1. zh → zh_tw: SimpleLocalize uses non-standard language code for zh-TW.
* 2. ko → kr: For historical reasons, kr is used instead of ko.
*/
function normalizeLng(lng) {
	if (lng === "zh") return "zh_tw";
	if (lng === "ko") return "kr";
	return lng;
}
/** Pure: resolve a (lng, ns) pair against a manifest to a fully-qualified
* hashed URL, or undefined if the manifest doesn't list it. */
function composeHashedUrl(manifest, lng, ns) {
	const path = manifest[lng]?.[ns];
	return path ? `${CDN_BASE}${path}` : void 0;
}
function manifestUnhashedUrl(lng, ns) {
	return `${CDN_BASE}/i18n-assets/${lng}/${ns}.json`;
}
/**
* Stateful cache around the i18n-assets manifest fetch. Re-fetches every
* `ttlMs` so freshly deployed translations land within ~1 minute (matches
* the CDN cache headers). Failed fetches cool down for `failureCooldownMs`
* to keep a CDN outage from multiplying outbound requests across
* back-to-back namespace loads — the cooldown applies whether or not we
* already have a (now-stale) cached manifest, since hashed bundles are
* immutable so serving a stale hashed URL still resolves to valid
* translation content. Concurrent `get()` calls share one inflight fetch.
*
* All side-effecting deps are injectable so the cache can be tested as a
* plain object — no module reset, no global stubbing.
*/
var ManifestCache = class {
	manifest;
	cachedAt = 0;
	lastFailureAt = 0;
	inflight;
	url;
	fetcher;
	now;
	ttlMs;
	failureCooldownMs;
	fetchTimeoutMs;
	constructor(opts = {}) {
		this.url = opts.url ?? I18N_MANIFEST_URL;
		this.fetcher = opts.fetcher ?? ((...args) => fetch(...args));
		this.now = opts.now ?? Date.now;
		this.ttlMs = opts.ttlMs ?? MANIFEST_TTL_MS;
		this.failureCooldownMs = opts.failureCooldownMs ?? MANIFEST_FAILURE_TTL_MS;
		this.fetchTimeoutMs = opts.fetchTimeoutMs ?? MANIFEST_FETCH_TIMEOUT_MS;
	}
	isFresh() {
		return this.manifest != null && this.now() - this.cachedAt < this.ttlMs;
	}
	/** Sync read; returns undefined when the cache is not fresh. */
	peek() {
		return this.isFresh() ? this.manifest : void 0;
	}
	/** Async read with TTL, inflight dedupe, and failure cooldown. */
	get() {
		if (this.isFresh()) return Promise.resolve(this.manifest);
		if (this.inflight) return this.inflight;
		if (this.lastFailureAt && this.now() - this.lastFailureAt < this.failureCooldownMs) {
			if (this.manifest) return Promise.resolve(this.manifest);
			return Promise.reject(/* @__PURE__ */ new Error("i18n manifest fetch cooldown"));
		}
		this.inflight = (async () => {
			try {
				const r = await this.fetcher(this.url, { signal: AbortSignal.timeout(this.fetchTimeoutMs) });
				if (!r.ok) throw new Error(`i18n manifest fetch failed: ${r.status}`);
				const m = await r.json();
				this.manifest = m;
				this.cachedAt = this.now();
				this.lastFailureAt = 0;
				return m;
			} catch (err) {
				this.lastFailureAt = this.now();
				throw err;
			} finally {
				this.inflight = void 0;
			}
		})();
		return this.inflight;
	}
};
const defaultCache = new ManifestCache();
/**
* Async i18next-http-backend `loadPath`. Resolves through the i18n-assets
* manifest so each translation file is served as a content-hashed bundle
* (cacheable forever). Falls back to the unhashed
* `/i18n-assets/{lng}/{ns}.json` alias (60s CDN TTL) if the manifest is
* unreachable or doesn't list the (lng, ns) pair — the chained backend's
* resourcesToBackend fallback then catches whatever HTTP still misses.
*
* Takes arrays to match i18next-http-backend's contract; only the first
* element of each is used.
*/
async function simpleLocalizeLoadPath(lngs, namespaces) {
	const ns = namespaces[0];
	const lng = normalizeLng(lngs[0]);
	try {
		const url = composeHashedUrl(await defaultCache.get(), lng, ns);
		if (url) return url;
	} catch {}
	return manifestUnhashedUrl(lng, ns);
}
/**
* Synchronous manifest lookup for sites that must emit a URL during
* render and can't await — e.g. `<link rel="preload">` injectors.
*
* Returns the hashed bundle URL only when the cache is fresh; once the
* TTL expires, returns `undefined` so callers fall back to the unhashed
* alias rather than emitting a hash from a stale manifest. Triggers a
* background refresh fire-and-forget so the next render can use the
* hashed URL again — no global warmer required, so processes that
* never call this helper (e.g. API workers) never hit the CDN.
*/
function manifestHashedUrl(lng, ns) {
	const manifest = defaultCache.peek();
	if (!manifest) {
		defaultCache.get().catch(() => {});
		return;
	}
	return composeHashedUrl(manifest, lng, ns);
}
//#endregion
export { ManifestCache, composeHashedUrl, manifestHashedUrl, manifestUnhashedUrl, normalizeLng, simpleLocalizeLoadPath };
